/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { ReactNode } from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { seoConfig, defaultMetaTags } from './seo-config';

interface SEOProviderProps {
  children: ReactNode;
}

/**
 * SEOProvider component that wraps the application with HelmetProvider
 * and provides default meta tags for all pages.
 * 
 * This component should be placed near the root of the application,
 * typically wrapping the main App component.
 * 
 * @example
 * ```tsx
 * <SEOProvider>
 *   <App />
 * </SEOProvider>
 * ```
 */
export function SEOProvider({ children }: SEOProviderProps) {
  const { t } = useTranslation('@pages/landing');
  const defaultDescription = t('pageDescription');

  return (
    <HelmetProvider>
      {/* Default meta tags for all pages */}
      <Helmet>
        {/* Basic meta tags */}
        <meta charSet="utf-8" />
        <meta name="viewport" content={defaultMetaTags.viewport} />
        <meta name="theme-color" content={defaultMetaTags.themeColor} />
        <meta name="robots" content={defaultMetaTags.robots} />
        
        {/* Default description — resolved via i18n to match the user's language */}
        <meta name="description" content={defaultDescription} />
        
        {/* Default Open Graph tags */}
        <meta property="og:site_name" content={seoConfig.siteName} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={seoConfig.defaultImage} />
        
        {/* Default Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        {seoConfig.twitterHandle && (
          <meta name="twitter:site" content={seoConfig.twitterHandle} />
        )}
        <meta name="twitter:image" content={seoConfig.defaultImage} />
        
        {/* Default title */}
        <title>{seoConfig.siteName}</title>
      </Helmet>
      
      {children}
    </HelmetProvider>
  );
}

export default SEOProvider;
